// @ts-nocheck
import { PropsWithChildren, useEffect, useRef } from 'react';
import '@material/web/textfield/filled-text-field.js';
import '@material/web/textfield/outlined-text-field.js';
import styled from '@emotion/styled';

export interface PhoneFieldResult {
  value: string;
  hasError: boolean;
  name: string;
}
export interface TextFieldProps extends PropsWithChildren {
  variant?: 'filled' | 'outlined';
  disabled?: boolean;
  error?: boolean;
  errorText?: string | null;
  label?: string;
  required?: boolean;
  value?: string | number | null;
  prefixText?: string;
  suffixText?: string;
  supportingText?: string;
  textDirection?: string;
  rows?: number;
  cols?: number;
  autocomplete?: string | null;
  max?: string;
  maxLength?: number;
  min?: string;
  minLength?: number;
  placeholder?: string;
  readOnly?: boolean;
  step?: number;
  type?:
    | 'text'
    | 'textarea'
    | 'email'
    | 'number'
    | 'password'
    | 'search'
    | 'tel'
    | 'url';
  name?: string;
  onInput?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onKeyPress?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  onClick?: () => void;
  children?: React.ReactNode;
  width?: string;
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  id?: string;
}
export const TextField = ({
  variant = 'outlined',
  disabled = false,
  errorText = '',
  error = false,
  label = '',
  required = false,
  value = '',
  prefixText,
  suffixText,
  supportingText,
  textDirection,
  rows,
  cols,
  min = null,
  max = null,
  minLength,
  maxLength,
  placeholder,
  readOnly = false,
  step = null,
  type = 'text',
  name,
  onInput,
  onKeyPress,
  onClick,
  children,
  width = '100%',
  onFocus,
  id = '',
  autocomplete = '',
  ...otherProps
}: TextFieldProps) => {
  const textFieldRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (textFieldRef.current && 'value' in textFieldRef.current) {
      (textFieldRef.current as any).value = value ?? '';
    }
  }, [value]);

  const props = { value, type, autocomplete };

  if (label) {
    props.label = label;
  }
  if (disabled) {
    props.disabled = disabled;
  }
  if (placeholder) {
    props.placeholder = placeholder;
  }
  if (minLength) {
    props.minLength = minLength;
  }
  if (maxLength) {
    props.maxLength = maxLength;
  }
  if (min !== null) {
    props.min = min;
  }
  if (autocomplete) {
    props.autocomplete = autocomplete;
  }

  if (max !== null) {
    props.max = max;
  }
  if (step !== null) {
    props.max = max;
  }
  if (rows) {
    props.rows = rows;
  }
  if (cols) {
    props.cols = cols;
  }
  if (name) {
    props.name = name;
  }
  if (prefixText) {
    props['prefix-text'] = prefixText;
  }
  if (suffixText) {
    props['suffix-text'] = suffixText;
  }
  if (supportingText) {
    props['supporting-text'] = supportingText;
  }
  if (textDirection) {
    props['text-direction'] = textDirection;
  }
  if (errorText) {
    props['error-text'] = errorText;
  }
  if (readOnly) {
    props.readOnly = readOnly;
  }
  if (required) {
    props.required = required;
  }
  if (error) {
    props.error = error;
  }

  if (onInput) {
    props.onInput = (event: React.ChangeEvent<HTMLInputElement>) => {
      onInput(event);
    };
  }
  if (onKeyPress) {
    props.onKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
      onKeyPress(event);
    };
  }

  if (id) {
    props.id = id;
  }

  if (onFocus) {
    props.onFocus = (event: React.FocusEvent<HTMLInputElement>) => {
      onFocus(event);
    };
  }

  if (type) {
    props.inputmethod = ['number', 'tel'].includes(type)
      ? 'numeric'
      : type === 'email'
        ? 'email'
        : 'text';
  }

  if (width) {
    props.style = { width };
  }
  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (onClick) {
      onClick();
    }
  };
  return (
    <StyledTextFieldWrapper>
      {variant === 'filled' ? (
        <md-filled-text-field ref={textFieldRef} {...otherProps} {...props}>
          {children}
        </md-filled-text-field>
      ) : (
        <md-outlined-text-field ref={textFieldRef} {...otherProps} {...props}>
          {children}
        </md-outlined-text-field>
      )}
      {/* When text field is disabled we cannot add any click listner to it. So
      we need to add overlay to it. */}
      {props.disabled && <StyledTextFieldOverlay onClick={handleClick} />}
    </StyledTextFieldWrapper>
  );
};

const StyledTextFieldWrapper = styled.div`
  position: relative;
`;

const StyledTextFieldOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  cursor: pointer;
  background-color: transparent;
  z-index: 1;
`;
